/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Placement } from '../../placement';
import { Logger } from '../../../logger';
import { debugPixel, generateUUID, isGptAvailable } from '../../../util';
import { isRenderableBannerSize } from '../../placement/constants';

import type { CampaignData, CampaignType } from '../../../types/';
import type { VendorFormat } from '../../../types/formats';
import type { PrebidAdUnit } from '../../../types/prebid';
import type { SiteConfig } from '../../config';

const logger = Logger.getInstance();
// Takeover buys (Adnami skins, Celtra rich-media banners) arrive as GAM line
// items rendered into an ordinary display slot, with the vendor engine
// injecting its click surfaces outside that slot's DOM — so GPT placements are
// the only ones that host them. Module-scope so the hook allocates nothing.
const SUPPORTED_VENDOR_FORMATS: readonly VendorFormat[] = ['adnami', 'celtra'];

// One-shot latch for the slot-without-setConfig pixel: API availability is a
// page-level property of the loaded GPT version, so one report covers every
// placement without emitting a pixel per placement per auction.
let slotSetConfigMissingReported = false;

// Ad unit path of a GPT slot, or null if the slot cannot supply one. Module
// scope so the render hot path allocates no closure per placement.
const slotAdUnitPathOf = (slot: googletag.Slot): string | null => {
  try {
    const path = slot.getAdUnitPath();
    return typeof path === 'string' && path.length > 0 ? path : null;
  } catch {
    return null;
  }
};

class GptPlacement extends Placement {
  // GAM response identifier of the last render this placement processed, used
  // to distinguish a genuine auction-less refresh render (new response) from a
  // duplicate slotRenderEnded dispatch for a render already handled. Null when
  // the event carried no identifier.
  private lastProcessedResponseIdentifier: string | null = null;
  // Ad unit path of the slot this placement is bound to, cached at bind time so
  // the render handler can test a candidate slot with one string compare rather
  // than a getter call per placement per render.
  private boundAdUnitPath: string | null = null;
  // Targeting keys this placement has set on its GPT slot via setConfig and not
  // yet retired. GPT offers no per-slot clear-all that spares publisher keys,
  // so retirement must name exactly these keys. Lazily allocated and reused
  // across auctions/renders — no per-render allocation at 50-placement scale.
  private appliedTargetingKeys: Set<string> | null = null;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig,
  ) {
    super(adUnit, adUnitTransformer, campaignData, siteConfig);
    logger.debug(`GPT Placement created for ${this.adUnitCode}`);
  }

  protected setSlot(): void {
    if (this.slot) {
      // A publisher that "refreshes" by destroying and redefining a slot leaves
      // this reference pointing at an object GPT has dropped. Holding it means
      // every later render for this ad unit fails the identity test in the
      // render handler, so keep the reference only while GPT still knows it.
      if (this.isSlotRegistered(this.slot)) {
        return;
      }
      logger.debug(`GPT slot for ${this.adUnitCode} is no longer registered; re-resolving`);
      this.slot = null;
      this.boundAdUnitPath = null;
      this.lastProcessedResponseIdentifier = null;
      // Targeting set via setConfig died with the destroyed slot object; the
      // replacement slot starts clean, so tracked keys must not be retired
      // against it later.
      this.appliedTargetingKeys?.clear();
      // A held selection died with those keys: it can never be consumed now.
      this.releaseSelectionHold('holdReleasedOther');
    }
    if (isGptAvailable()) {
      logger.debug(`Determining GPT slot for adUnit: ${this.adUnitCode}`);
      try {
        const gptSlots = window.googletag.pubads().getSlots();

        const matchingSlot = gptSlots.find((slot) => {
          let slotElementId = slot.getSlotElementId();
          let slotAdUnitPath = slot.getAdUnitPath();
          logger.debug(`GPT slot available: ${slotAdUnitPath} (${slotElementId})`);
          return (
            this.adUnitCode == slotElementId ||
            this.adUnitCode == slotAdUnitPath ||
            slotAdUnitPath.endsWith(`/${this.adUnitCode}`)
          );
        });

        this.slot = matchingSlot || null;
        this.boundAdUnitPath = null;
        if (this.slot) {
          logger.info(`GPT slot located for ${this.adUnitCode}`);
        }
      } catch (e) {
        logger.error(`Error getting GPT slot for ${this.adUnitCode}: ${e}`);
        this.slot = null;
      }
    } else {
      logger.error(`GPT not available, unable to determine slot for adUnit: ${this.adUnitCode}`);
    }
  }

  /**
   * Push key-values straight onto the GPT slot via slot.setConfig({targeting})
   * as soon as they are known (select response processed, rules run), instead
   * of waiting for the publisher's Prebid targeting flush — some wrappers
   * (e.g. Future's bordeaux) only flush ad units with Prebid bids, or skip the
   * flush entirely on auction timeout. GPT merges targeting per key across
   * setConfig calls, so the later Prebid flush of the identical values is a
   * no-op, and values set before pubads().refresh() ride that ad request.
   *
   * Returns true when the slot-level path delivered (or had nothing to do),
   * false when it could not (no slot, no setConfig API, call threw) and the
   * Prebid flush remains the delivery mechanism.
   */
  public applyKeyValuesToAdServer(campaignTypes: CampaignType[]): boolean {
    const keyValues = this.getKeyValuesForCampaignType(campaignTypes);
    const applied = this.appliedTargetingKeys;
    if (keyValues.length === 0 && (!applied || applied.size === 0)) {
      // Nothing to set and nothing to retire.
      return true;
    }
    this.setSlot();
    if (this.slot === null) {
      logger.debug(`No GPT slot bound for ${this.adUnitCode}, key-values not applied to slot`);
      return false;
    }
    if (typeof this.slot.setConfig !== 'function') {
      // Legacy GPT without slot-level setConfig: the Prebid flush remains the
      // only delivery path. Availability is a page-level property, report once.
      if (!slotSetConfigMissingReported) {
        slotSetConfigMissingReported = true;
        debugPixel({ en: 'slotCfgUnavailable', adUnitCode: this.adUnitCode });
      }
      return false;
    }
    const targeting: Record<string, string | null> = {};
    keyValues.forEach((kv) => {
      // Campaigns emit previously-set keys with '' values to overwrite
      // Prebid's targeting cache; at slot level the analogue is removal — an
      // empty string would send a bare `key=` to the ad server.
      targeting[kv.key] = kv.value === '' ? null : kv.value;
    });
    // Retire keys applied in an earlier auction that this selection no longer
    // produces (select returned nothing for them, no intervening render).
    applied?.forEach((key) => {
      if (!(key in targeting)) {
        targeting[key] = null;
      }
    });
    try {
      this.slot.setConfig({ targeting });
    } catch (e) {
      logger.error(`slot.setConfig failed for ${this.adUnitCode}: ${e}`);
      // Tracking is deliberately unchanged: nothing was applied, and a later
      // successful apply must still retire whatever the slot really holds.
      return false;
    }
    const tracked = this.appliedTargetingKeys ?? (this.appliedTargetingKeys = new Set<string>());
    tracked.clear();
    keyValues.forEach((kv) => {
      if (kv.value !== '') {
        tracked.add(kv.key);
      }
    });
    this.markKeyValuesDelivered();
    logger.info(`Applied ${tracked.size} key-values to GPT slot for ${this.adUnitCode}`);
    return true;
  }

  protected getAdUnitPath(): string | null {
    if (!this.slot) {
      return null;
    }
    try {
      const path = this.slot.getAdUnitPath();
      return typeof path === 'string' && path.length > 0 ? path : null;
    } catch (e) {
      logger.error(`GPT slot failed to return ad unit path for ${this.adUnitCode}: ${e}`);
      return null;
    }
  }

  protected getSupportedVendorFormats(): readonly VendorFormat[] {
    return SUPPORTED_VENDOR_FORMATS;
  }

  protected setWrappingDomElement(): void {
    if (this.slot) {
      this.wrappingDomElement = document.getElementById(this.slot.getSlotElementId() as string);
    }
  }

  protected determineVisibility(): void {
    const size = this.getSmallestSupportedSize();
    if (!size || this.slot === null) {
      return;
    }

    let elementId: string;
    try {
      elementId = this.slot.getSlotElementId();
    } catch (e) {
      logger.error(`GPT Slot failed to return element ID: ${e}`);
      return;
    }

    const element = document.getElementById(elementId);
    if (!element) {
      logger.info(
        `GPT adunit '${this.adUnitCode}' Slot element reference '${elementId}' not found`,
      );
      return;
    }
    const isVisible = this.checkElementVisibility(element, size);
    this.isVisible(isVisible);
  }

  protected setSupportedSizes(): void {
    try {
      this.slot.getSizes().forEach((size: any) => {
        // GPT represents native/flex capability as the literal string 'fluid';
        // publishers also conventionally declare native slots as 1x1. Neither
        // is a renderable banner size, so they only mark native capability.
        if (size === 'fluid' || (size?.width === 1 && size?.height === 1)) {
          this.supportsNative = true;
          return;
        }
        // Every renderable size the slot advertises counts, custom direct-sold
        // sizes included — ad-level placement.sizes rules imported from the ad
        // server reference them (see isRenderableBannerSize)
        if (isRenderableBannerSize(size?.width, size?.height)) {
          this.addSupportedSize({ width: size.width, height: size.height });
        }
      });
    } catch (e) {
      logger.warn(`Error determining GPT slot size: ${e}`);
    }
  }

  protected addListeners(): void {
    logger.debug(`Adding GPT listeners for placement: ${this.adUnitCode}`);
    this.addSlotRequestedListener();
    this.addSlotRenderedListener();
    this.addViewableImpressionListener();
    this.addPrebidAdRenderedListener();
  }

  private addSlotRequestedListener(): void {
    if (this.slot === null) {
      return;
    }
    try {
      logger.debug(`Adding slotRequested listener for GPT for: ${this.adUnitCode}`);
      const slotRequestedEventHandler = (event: googletag.events.SlotRequestedEvent) => {
        if (event.slot !== this.slot && !this.adoptReplacementSlot(event.slot)) {
          return;
        }
        // Applied targeting rides this request: consumption/retirement happen
        // here, not at slotRenderEnded (see consumeHeldKeyValues).
        this.consumeHeldKeyValues();
      };
      window.googletag.pubads().addEventListener('slotRequested', slotRequestedEventHandler);
    } catch (e) {
      logger.error(`Error adding GPT slotRequested listener to ${this.adUnitCode}: ${e}`);
    }
  }

  private addSlotRenderedListener(): void {
    if (this.slot === null) {
      return;
    }
    try {
      logger.debug(`Adding slot render ended listener for GPT for: ${this.adUnitCode}`);
      const slotRenderedEventHandler = (event: googletag.events.SlotRenderEndedEvent) => {
        if (event.slot !== this.slot && !this.adoptReplacementSlot(event.slot)) {
          return;
        }
        // A slotRenderEnded for this slot means GPT has replaced (or emptied)
        // the previously rendered ad: its iframe is gone from the DOM, so the
        // listeners, impression context and latches describing it must be torn
        // down whether or not we go on to track the new render. Applied slot
        // targeting is deliberately NOT retired here — retirement happens at
        // the slotRequested event that consumed it.
        this.resetStateForNewRender();
        // A render arriving with the per-opportunity latch already consumed is
        // an auction-less refresh (publisher-driven googletag refresh with no
        // Prebid auction). Mint a placement-local opportunity so the new
        // render's impression and interactions are counted; without one the
        // slot would go dark until the next Prebid auction.
        if (this.slotRenderedForOpportunity && this.isTrackableRefreshRender(event)) {
          this.mintRenderOpportunity();
        }
        if (!this.slotRenderedForOpportunity) {
          this.slotRenderedForOpportunity = true;
          logger.debug(
            `GPT slot rendered for: ${event.slot.getAdUnitPath()},` +
              ` handled by placement (${this.adUnitCode}), line: ${event.lineItemId}`,
          );

          const hasSize = Array.isArray(event.size) && event.size.length > 1;
          this.renderedSlotSize = {
            // @ts-ignore
            width: hasSize ? (event.size[0] as number) : 0,
            // @ts-ignore
            height: hasSize ? (event.size[1] as number) : 0,
          };

          if (!event.isEmpty && !event.isBackfill && event.advertiserId && event.lineItemId) {
            if (this.isBlankFriendlyRender()) {
              // Skin/takeover roadblocks blank their companion slots with 1x1
              // creatives that carry real advertiser/line ids and would
              // otherwise record as impressions. They always render 1x1 in
              // friendly iframes (the skin creative set has SafeFrame
              // disabled), while genuine fills are SafeFramed on these
              // properties, and 1x1-reporting rich formats (e.g. web
              // interstitials) are not simple friendly image frames.
              logger.info(
                `Ignoring 1x1 friendly-iframe blank render for ${this.adUnitCode}, line: ${event.lineItemId}`,
              );
              debugPixel({
                en: 'blankRenderIgnored',
                adUnitCode: this.adUnitCode,
                lineItemId: String(event.lineItemId),
              });
              return;
            }
            const advertiserId = String(event.advertiserId);
            const creativeId = event.creativeId ? String(event.creativeId) : '';
            const lineId = String(event.lineItemId);
            const renderedAdData = {
              advertiserId: advertiserId,
              lineItemId: lineId,
              creativeId: creativeId,
              isProgrammaticBuy: false,
              isPrebidAd:
                this.getSiteConfig().directLearningAdvertiserIgnoreList.includes(advertiserId),
              responseIdentifier: event.responseIdentifier
                ? String(event.responseIdentifier)
                : generateUUID(),
              size: this.renderedSlotSize,
            };
            this.lastProcessedResponseIdentifier = event.responseIdentifier
              ? String(event.responseIdentifier)
              : null;
            this.handleRenderedAdData(renderedAdData);
          } else {
            logger.debug(
              `GPT slot rendered backfill or empty: ${event.slot.getAdUnitPath()}, ${this.adUnitCode}`,
            );
            // Case of unlikely missing line ID not covered
          }
        }
      };

      window.googletag.pubads().addEventListener('slotRenderEnded', slotRenderedEventHandler);
    } catch (e) {
      logger.error(`Error adding GPT slotRenderEnded listener to ${this.adUnitCode}: ${e}`);
    }
  }

  // True when the render that just completed is a 1x1 creative in a friendly
  // (same-origin) iframe. SafeFrame or cross-origin renders fail open: if the
  // frame document is not inspectable we never suppress.
  private isBlankFriendlyRender(): boolean {
    const size = this.renderedSlotSize;
    if (!size || size.width !== 1 || size.height !== 1) {
      return false;
    }
    const frame = this.wrappingDomElement?.querySelector<HTMLIFrameElement>(
      'iframe[id^="google_ads_iframe_"]',
    );
    if (!frame) {
      return false;
    }
    try {
      return frame.contentDocument !== null;
    } catch (e) {
      return false;
    }
  }

  // Ad unit path of the currently bound slot, memoised on first use so the
  // render hot path costs a string compare rather than a getter call. Resolved
  // lazily because `slot` is not only assigned via setSlot().
  private getBoundAdUnitPath(): string | null {
    if (this.boundAdUnitPath === null && this.slot !== null) {
      this.boundAdUnitPath = slotAdUnitPathOf(this.slot);
    }
    return this.boundAdUnitPath;
  }

  protected retireAppliedAdServerTargeting(): void {
    this.retireAppliedTargetingKeys();
  }

  // Unset exactly the keys this placement applied via setConfig ({key: null}
  // per key — GPT has no clear-all that spares publisher keys). Fires when a
  // request has consumed the targeting: it must not ride any later ad request
  // without a fresh selection.
  private retireAppliedTargetingKeys(): void {
    const applied = this.appliedTargetingKeys;
    if (!applied || applied.size === 0) {
      return;
    }
    if (this.slot !== null && typeof this.slot.setConfig === 'function') {
      const targeting: Record<string, null> = {};
      applied.forEach((key) => {
        targeting[key] = null;
      });
      try {
        this.slot.setConfig({ targeting });
        logger.debug(`Retired ${applied.size} slot targeting keys for ${this.adUnitCode}`);
      } catch (e) {
        logger.error(`Failed to retire slot targeting for ${this.adUnitCode}: ${e}`);
      }
    }
    applied.clear();
  }

  // True while GPT still lists this slot. A destroyed slot keeps answering its
  // getters, so identity alone cannot tell a live slot from a dropped one.
  private isSlotRegistered(slot: googletag.Slot): boolean {
    try {
      return window.googletag.pubads().getSlots().indexOf(slot) !== -1;
    } catch (e) {
      logger.error(`Error checking GPT slot registration for ${this.adUnitCode}: ${e}`);
      // Assume still registered: dropping a live binding costs every later
      // render on this placement, while keeping a dead one costs nothing extra.
      return true;
    }
  }

  /**
   * Re-bind to the slot that replaced this placement's own, returning whether
   * the incoming render now belongs to this placement.
   *
   * Publishers that refresh by destroying and redefining a slot hand GPT a new
   * slot object for the same ad unit path. The binding is resolved once, so
   * without re-binding every post-refresh render fails the identity test in the
   * render handler and is dropped — silently, because the skipped-render
   * telemetry sits downstream of that test. The ad server counts those renders,
   * so the placement under-reports for the rest of the page's life.
   *
   * Adoption is refused while this placement's own slot is still registered: a
   * page may hold several live slots on one ad unit path (publishers reuse a
   * capped incremental name such as `incr-mpu-10`), and each must stay with the
   * placement that resolved it rather than be claimed by the first one to see a
   * render.
   */
  private adoptReplacementSlot(candidate: googletag.Slot): boolean {
    // Runs for every placement on every render, so reject on a cached string
    // compare before touching the slot registry.
    if (this.slot === null) {
      return false;
    }
    const boundPath = this.getBoundAdUnitPath();
    if (boundPath === null || slotAdUnitPathOf(candidate) !== boundPath) {
      return false;
    }
    if (this.isSlotRegistered(this.slot)) {
      return false;
    }
    logger.info(
      `GPT slot for ${this.adUnitCode} was replaced; re-binding to the new slot object`
    );
    this.slot = candidate;
    this.boundAdUnitPath = boundPath;
    // Response identifiers are scoped to the slot that produced them; carrying
    // one across would let the first render on the new slot be mistaken for a
    // duplicate and dropped.
    this.lastProcessedResponseIdentifier = null;
    // Targeting applied via setConfig died with the destroyed slot; retiring
    // the tracked keys against the replacement would be a wasted call at best.
    this.appliedTargetingKeys?.clear();
    // A held selection died with those keys: it can never be consumed now.
    this.releaseSelectionHold('holdReleasedOther');
    this.setWrappingDomElement();
    return true;
  }

  // A refresh render only earns a minted opportunity when it would pass the
  // tracked-render predicate below AND is not a duplicate dispatch of the
  // render already processed. GAM response identifiers are unique per ad
  // response, so a legitimate re-serve of the same creative still counts; an
  // event with no identifier cannot be deduplicated and is counted.
  private isTrackableRefreshRender(event: googletag.events.SlotRenderEndedEvent): boolean {
    if (event.isEmpty || event.isBackfill || !event.advertiserId || !event.lineItemId) {
      return false;
    }
    return (
      !event.responseIdentifier ||
      String(event.responseIdentifier) !== this.lastProcessedResponseIdentifier
    );
  }

  private addViewableImpressionListener(): void {
    if (this.slot === null) {
      return;
    }
    try {
      const viewableImpressionEventHandler = (event: googletag.events.ImpressionViewableEvent) => {
        if (event.slot === this.slot) {
          logger.debug(
            `impressionViewable dispatched by GPT for: ${event.slot.getAdUnitPath()}, ${this.adUnitCode}`,
          );
          this.dispatchViewableImpression();
        }
      };
      logger.debug(`Adding impressionViewable listener for GPT for: ${this.adUnitCode}`);
      window.googletag
        .pubads()
        .addEventListener('impressionViewable', viewableImpressionEventHandler);
    } catch (e) {
      logger.error(`Error adding GPT impressionViewable listener to ${this.adUnitCode}: ${e}`);
    }
  }

  protected addAdInteractionListener(): void {
    this.addIframedAdInteractionListener();
  }

  // Interaction listeners are deliberately NOT removed per opportunity: an
  // auction is not a render, and the previously rendered ad remains clickable
  // until GPT actually replaces it. Teardown happens in resetStateForNewRender,
  // invoked from the slotRenderEnded handler above. Duplicate AdInteraction
  // dispatches from a stale window-blur listener are prevented by the
  // per-render adInteractionLoggedForRender latch, which now also survives
  // auction boundaries.
}

export { GptPlacement };
